import React, { useRef, useState, useEffect } from "react";
import { toast } from "react-toastify";
import styles from "./BlockModal.module.scss";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Loader from "../../../components/Loader/Loader";

const UnblockModal = ({
  onClose,
  initialSport,
  availableSports,
  fetchInitialData,
}) => {
  const [sport, setSport] = useState(initialSport || "");
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Format date to YYYY-MM-DD
  const formatDate = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Format time to 12-hour format with AM/PM
  const formatTime = (time) => {
    if (!time) return "";
    const [hour, minute] = time.split(":");
    const hourNum = parseInt(hour, 10);
    const period = hourNum >= 12 ? "PM" : "AM";
    const formattedHour = hourNum % 12 || 12;
    return `${formattedHour}:${minute} ${period}`;
  };

  // Fetch blocked slots for the selected sport
  const fetchBlockedSlots = async () => {
    if (!sport) return;
    setLoading(true);
    setError(null);
    const url = `https://ballersapi.ballerslife.co/api/v1/facility_admin/get_all_blocked_slots?sport=${sport}`;
    const accessToken = window.localStorage.getItem("access_token");

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();
      if (response.status === 200) {
        // Sort blocked_slots by booking_date and then blocked_slot
        const sortedSlots = (data.blocked_slots || []).sort((a, b) => {
          const dateTimeA = new Date(`${a.booking_date}T${a.blocked_slot}`);
          const dateTimeB = new Date(`${b.booking_date}T${b.blocked_slot}`);
          return dateTimeA - dateTimeB;
        });
        setBlockedSlots(sortedSlots);
      } else {
        setError(data.detail || "Failed to fetch blocked slots.");
        toast.error(data.detail || "Failed to fetch blocked slots.");
      }
    } catch (err) {
      setError("An error occurred while fetching blocked slots.");
      toast.error("An error occurred while fetching blocked slots.");
    } finally {
      setLoading(false);
    }
  };

  // Unblock a slot by blocked_slot_id
  const handleUnblock = async (blockedSlotId) => {
    const url = `https://ballersapi.ballerslife.co/api/v1/facility_admin/delete_blocked_slot_by_id?blocked_slot_id=${blockedSlotId}`;
    const accessToken = window.localStorage.getItem("access_token");

    try {
      setLoading(true);
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();
      if (response.status === 200) {
        toast.success(data.message || "Slot unblocked successfully.");
        // Remove the unblocked slot from the UI
        fetchInitialData(); // Fetch initial data after getting blocked slots
        setBlockedSlots((prev) =>
          prev.filter((slot) => slot.blocked_slot_id !== blockedSlotId)
        );
      } else {
        toast.error(data.detail || "Failed to unblock slot.");
      }
    } catch (err) {
      toast.error("An error occurred while unblocking the slot.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch blocked slots when sport changes
  useEffect(() => {
    fetchBlockedSlots();
  }, [sport]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className={styles.modal}>
          <div className={styles["modal-content-block"]}>
            <h3>Unblock Slot</h3>
            <button
              className={styles["close-btn"]}
              onClick={onClose}
              disabled={loading}
            >
              ×
            </button>
            <div
              className={`${styles["form-group"]} ${styles["form-group-ubModal"]}`}
            >
              <label>Sport</label>
              <select
                value={sport}
                onChange={(e) => setSport(e.target.value)}
                disabled={availableSports.length === 0 || loading}
                className={styles["select-ubModal"]}
              >
                {availableSports.length === 0 ? (
                  <option value="">No sports available</option>
                ) : (
                  availableSports.map((sportItem) => (
                    <option key={sportItem} value={sportItem}>
                      {sportItem.charAt(0).toUpperCase() + sportItem.slice(1)}
                    </option>
                  ))
                )}
              </select>
            </div>
            {loading ? (
              <div className={styles["loading-ubModal"]}>Loading...</div>
            ) : error ? (
              <div className={styles["error-ubModal"]}>{error}</div>
            ) : blockedSlots.length === 0 ? (
              <div className={styles["no-slots-ubModal"]}>
                No blocked slots found for {sport}.
              </div>
            ) : (
              <div className={styles["blocked-slots-ubModal"]}>
                {blockedSlots.map((slot) => (
                  <div
                    key={slot.blocked_slot_id}
                    className={styles["slot-item-ubModal"]}
                  >
                    <div className={styles["slot-details-ubModal"]}>
                      <p>
                        <strong>Sport:</strong>{" "}
                        {slot.sport.charAt(0).toUpperCase() +
                          slot.sport.slice(1)}
                      </p>
                      <p>
                        <strong>Date:</strong> {slot.booking_date}
                      </p>
                      <p>
                        <strong>Time:</strong> {formatTime(slot.blocked_slot)}
                      </p>
                      <p>
                        <strong>Duration:</strong> {slot.duration}{" "}
                        {slot.duration === 1 ? "hour" : "hours"}
                      </p>
                    </div>
                    <button
                      className={styles["unblock-btn-ubModal"]}
                      onClick={() => handleUnblock(slot.blocked_slot_id)}
                      disabled={loading}
                    >
                      Unblock
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default UnblockModal;
